/**
 * useTableData Hook
 *
 * Fetches table data from an API endpoint using React Query.
 * Combines base filters, user filters, search, sort, and pagination
 * into a single API call.
 *
 * This hook is designed for the self-contained MetadataTable component,
 * encapsulating all data fetching logic.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { tableService } from '@/services/tableService'
import type { Filter, OrderBy, SearchOptions } from '@/types/api.types'
import type { FieldMetadata, SearchConfig, LifecycleMetadata } from '@/services/metadataService'
import type { UseTableDataResult } from '../types/selfContained.types'

/**
 * Options for useTableData hook
 */
interface UseTableDataOptions {
  /**
   * API endpoint path (e.g., '/offerings', '/audit_logs')
   */
  endpoint: string

  /**
   * Current page (0-indexed)
   */
  page: number

  /**
   * Number of rows per page
   */
  pageSize: number

  /**
   * Sort field key (null for no sort)
   */
  sortKey: string | null

  /**
   * Sort direction
   */
  sortDirection: 'asc' | 'desc' | null

  /**
   * User-applied filters
   */
  filters: Filter[]

  /**
   * Search term (applied across searchable fields)
   */
  searchTerm: string

  /**
   * Base filters that are always applied (e.g., exclude deleted)
   */
  baseFilters?: Filter[]

  /**
   * Field metadata for search field detection
   */
  fields?: Record<string, FieldMetadata>

  /**
   * Search configuration from metadata (overrides heuristic)
   * - SearchConfig object: use specified fields
   * - false: disable search (return empty filters)
   * - undefined: use heuristic (fallback to current behavior)
   */
  searchConfig?: SearchConfig | false

  /**
   * Default sort from metadata (used when no user sort is active)
   */
  defaultSort?: Array<{ key: string; direction: 'asc' | 'desc' }>

  /**
   * Custom React Query key (default: ['tableData', endpoint])
   */
  queryKey?: string[]

  /**
   * Whether to enable the query (default: true)
   */
  enabled?: boolean

  /**
   * Called when fetch error occurs
   */
  onError?: (error: Error) => void

  /**
   * External order_by that bypasses internal sort building.
   * When provided, sortKey/sortDirection/defaultSort are ignored.
   */
  externalOrderBy?: { key: string; direction: 'asc' | 'desc' }[]

  /**
   * Lifecycle metadata for auto-filtering valid states.
   * When provided and enabled, automatically adds a filter to only show
   * records with valid lifecycle states (derived from action fromStates/toState).
   * This prevents showing records with garbage/invalid state values.
   */
  lifecycle?: LifecycleMetadata

  /**
   * Include soft-deleted records in results.
   * When true, bypasses the service layer's default deletion filter.
   */
  includeDeleted?: boolean
}

/**
 * Get valid lifecycle states from lifecycle metadata.
 *
 * Priority:
 * 1. Use lifecycle.states if provided by backend (authoritative list from StateConfig)
 * 2. Fall back to deriving from actions' fromStates/toState (legacy behavior)
 *
 * The backend sends the complete states list from StateConfig, which includes
 * all valid states (e.g., draft, pending_approval, active, expired, superseded).
 * This is more reliable than deriving from filtered actions.
 *
 * @param lifecycle - Lifecycle metadata from backend
 * @returns Array of valid state strings, or null if lifecycle not enabled
 */
function deriveValidLifecycleStates(lifecycle?: LifecycleMetadata): string[] | null {
  if (!lifecycle?.enabled) {
    return null
  }

  // Prefer the authoritative states list from backend if available
  // This comes directly from StateConfig.states and includes ALL valid states
  if (lifecycle.states && lifecycle.states.length > 0) {
    return lifecycle.states
  }

  // Fall back to deriving from actions (legacy behavior)
  if (!lifecycle.actions) {
    return null
  }

  const states = new Set<string>()

  Object.values(lifecycle.actions).forEach((action) => {
    // Add all fromStates
    action.fromStates?.forEach((s) => states.add(s))
    // Add toState if defined
    if (action.toState) {
      states.add(action.toState)
    }
  })

  // Return null if no states found (shouldn't happen with valid lifecycle)
  if (states.size === 0) {
    return null
  }

  return Array.from(states)
}

/**
 * Build search filter for a term across searchable fields.
 *
 * @param searchTerm - The search term
 * @param fields - Field metadata to determine searchable fields (used for heuristic fallback)
 * @param searchConfig - Search configuration from metadata (overrides heuristic)
 * @returns Array of filters to apply
 *
 * Backend filter DSL supports OR logic via wrapper format:
 * {
 *   constraint_type: "OR",
 *   filters: [
 *     {key: "name", constraint_type: "CONTAINS", value: "widget"},
 *     {key: "sku", constraint_type: "CONTAINS", value: "widget"}
 *   ]
 * }
 *
 * Strategy:
 * - Single field: Return simple CONTAINS filter
 * - Multiple fields: Return OR wrapper with CONTAINS for each field
 */
function buildSearchFilters(
  searchTerm: string,
  fields?: Record<string, FieldMetadata>,
  searchConfig?: SearchConfig | false
): Filter[] {
  if (!searchTerm.trim()) {
    return []
  }

  // If search is explicitly disabled, return empty filters
  if (searchConfig === false) {
    return []
  }

  // If searchConfig is provided with fields, use them
  if (searchConfig && searchConfig.fields && searchConfig.fields.length > 0) {
    // Single field - no OR wrapper needed
    if (searchConfig.fields.length === 1) {
      return [
        {
          key: searchConfig.fields[0],
          constraint_type: 'CONTAINS' as const,
          value: searchTerm.trim(),
        },
      ]
    }

    // Multiple fields - build OR wrapper
    return [
      {
        constraint_type: 'OR' as const,
        filters: searchConfig.fields.map((field) => ({
          key: field,
          constraint_type: 'CONTAINS' as const,
          value: searchTerm.trim(),
        })),
      },
    ]
  }

  // Fall back to heuristic if no searchConfig provided
  if (!fields) {
    return []
  }

  // Find searchable text fields (string type, visible)
  // Note: system flag is for write protection only, not visibility
  const searchableFields = Object.entries(fields)
    .filter(([, field]) => {
      const isTextType = ['string', 'str'].includes(field.py_type)
      const isVisible = field.is_visible !== false
      return isTextType && isVisible
    })
    .map(([key]) => key)

  // Search on 'name' field if it exists, or first searchable field
  const searchField = searchableFields.includes('name')
    ? 'name'
    : searchableFields[0]

  if (!searchField) {
    return []
  }

  return [
    {
      key: searchField,
      constraint_type: 'CONTAINS' as const,
      value: searchTerm.trim(),
    },
  ]
}

/**
 * Hook to fetch table data with all filters applied.
 *
 * @param options - Configuration options
 * @returns Data, total count, loading state
 *
 * @example Basic usage
 * ```tsx
 * const {
 *   data,
 *   total,
 *   isLoading,
 *   error,
 *   refetch,
 * } = useTableData({
 *   endpoint: '/offerings',
 *   page: 0,
 *   pageSize: 50,
 *   sortKey: 'name',
 *   sortDirection: 'asc',
 *   filters: [],
 *   searchTerm: '',
 *   baseFilters: [{ key: 'deletedAt', constraint_type: 'IS_NULL' }],
 * })
 * ```
 */
export function useTableData<T = Record<string, unknown>>({
  endpoint,
  page,
  pageSize,
  sortKey,
  sortDirection,
  filters,
  searchTerm,
  baseFilters = [],
  fields,
  searchConfig,
  defaultSort = [],
  queryKey,
  enabled = true,
  onError,
  externalOrderBy,
  lifecycle,
  includeDeleted = false,
}: UseTableDataOptions): UseTableDataResult<T> {
  // Derive valid lifecycle states from actions metadata
  const validLifecycleStates = useMemo(
    () => deriveValidLifecycleStates(lifecycle),
    [lifecycle]
  )

  // Build combined filters (including lifecycle state filter if applicable)
  const combinedFilters = useMemo(() => {
    const searchFilters = buildSearchFilters(searchTerm, fields, searchConfig)
    const allFilters = [...baseFilters, ...filters, ...searchFilters]

    // Auto-add lifecycle state filter to exclude invalid/garbage states
    if (validLifecycleStates && lifecycle?.stateField) {
      allFilters.push({
        key: lifecycle.stateField,
        constraint_type: 'IN' as const,
        values: validLifecycleStates,
      })
    }

    return allFilters
  }, [baseFilters, filters, searchTerm, fields, searchConfig, validLifecycleStates, lifecycle?.stateField])

  // Build order_by
  const orderBy = useMemo<OrderBy[]>(() => {
    // External order_by takes highest priority (for parent-controlled sorting)
    if (externalOrderBy && externalOrderBy.length > 0) {
      return externalOrderBy
    }

    // User sort takes priority (column header clicks)
    if (sortKey && sortDirection) {
      return [{ key: sortKey, direction: sortDirection }]
    }

    // Fall back to default sort from metadata
    if (defaultSort.length > 0) {
      return defaultSort.map((s) => ({ key: s.key, direction: s.direction }))
    }

    // Ultimate fallback
    return [{ key: 'modifiedAt', direction: 'desc' }]
  }, [externalOrderBy, sortKey, sortDirection, defaultSort])

  // Build search options
  const searchOptions = useMemo<SearchOptions>(
    () => ({
      filters: combinedFilters,
      order_by: orderBy,
      limit: pageSize,
      offset: page * pageSize,
      // Include deleted records when trash view is active
      ...(includeDeleted && { includeDeleted: true }),
    }),
    [combinedFilters, orderBy, pageSize, page, includeDeleted]
  )

  // Build query key
  const effectiveQueryKey = useMemo(
    () => queryKey ?? ['tableData', endpoint, searchOptions],
    [queryKey, endpoint, searchOptions]
  )

  // Fetch data
  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: effectiveQueryKey,
    queryFn: async () => {
      const result = await tableService.search<T>(endpoint, searchOptions)
      if (!result.success) {
        const err = new Error(result.message || 'Failed to fetch data')
        onError?.(err)
        throw err
      }
      return result
    },
    enabled,
    staleTime: 1000 * 60, // 1 minute - data may change frequently
  })

  // Extract data and metadata from response
  const data: T[] = response?.data ?? []
  const meta = response?.meta ?? {}

  // Extract pagination metadata with sensible defaults
  const total: number = meta.total ?? 0
  const returned: number = meta.returned ?? data.length
  const hasMore: boolean = meta.hasMore ?? false
  const serverOffset: number = meta.offset ?? page * pageSize
  const serverLimit: number = meta.limit ?? pageSize

  return {
    data,
    total,
    returned,
    hasMore,
    serverOffset,
    serverLimit,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  }
}
